import { batch } from 'react-redux';
import { AppThunk } from '../../store';
import {
  IAnalysisQuery,
  IAnalysisQueryVariables,
  INamedCohortQueryQuery,
  INamedCohortQueryQueryVariables,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { GET_ANALYSIS } from '../../../utils/apollo/analysis';
import { assertNotNil } from '../../../utils/types';
import {
  builderConfigByQueryType,
  prepareBuilderInitialConfig,
} from '../../../utils/builder';
import { getAnalysisInfoFetchByType } from '../../../utils/hooks/useAnalysisInfoFetch/utils';
import {
  populateCachedCohort,
  populateConfig,
  populateNamedBuilderCohort,
  populateNextStep,
  resetBuilder,
  setBuilderInitialized,
} from './index';
import { populateFunnelState } from '../../../components/Builder/utils/builderUtils';
import { clearCachedFunnel, clearNextStep } from '../cachedEntities';
import { GET_NAMED_COHORT } from '../../../utils/apollo/cohorts';
import { RowType } from './types';

interface IPopulateAnalysisAndConfigPayload {
  analysisId: string;
}

/**
 * A thunk that runs a certain actions for Builder with report:
 * 1. load analysis data;
 * 2. once loaded, get schemaId and load config;
 * 3. once config loaded - populate values to redux and show Builder.
 */
export const populateAnalysisAndConfigThunk =
  (payload: IPopulateAnalysisAndConfigPayload): AppThunk =>
  async (dispatch, _, { apolloClient }) => {
    dispatch(setBuilderInitialized(false));

    const { analysisId } = payload;
    // First of all load analysis data
    const { data } = await apolloClient.query<
      IAnalysisQuery,
      IAnalysisQueryVariables
    >({
      query: GET_ANALYSIS,
      variables: {
        analysisId,
      },
    });
    assertNotNil(data.analysis);
    const { analysis } = data;
    const { queryType, query } = analysis;

    const configQuery = builderConfigByQueryType[queryType];
    const analysisInfoFetchHandler = getAnalysisInfoFetchByType[queryType];

    const schemaId = query.schema.id;

    // Once we have analysis data, load config with a proper schemaId
    // TODO: infer types from builderConfigByQueryType
    const { data: configData } = await apolloClient.query({
      query: configQuery,
      variables: {
        schemaId,
      },
    });

    batch(() => {
      dispatch(resetBuilder());
      dispatch(
        populateConfig(
          prepareBuilderInitialConfig({ queryType, schemaId, ...configData }),
        ),
      );
      analysisInfoFetchHandler(dispatch, analysis);
      dispatch(setBuilderInitialized(true));
    });
  };

interface IPlainBuilderPayload {
  queryType: QueryType;
  defaultBuilderSchemaId: string;
}

/**
 * A thunk that runs a certain actions for plain Builder.
 * Handles cachedEntities like: cachedCohort, cachedFunnel, and next step.
 *
 * a). In case if cached data is present, we have to take schemaId from it,
 * then load config, and then populate config and cached data to the Builder.
 *
 * b). If we don't have any cached data, we simply load the config with
 * users default schemaId, populate it and show the Builder.
 */
export const populatePlainBuilderThunk =
  (payload: IPlainBuilderPayload): AppThunk =>
  async (dispatch, getState, { apolloClient }) => {
    const { cachedEntities } = getState();
    const { cachedCohort, cachedFunnel, nextStep } = cachedEntities;
    const { queryType, defaultBuilderSchemaId } = payload;
    const isFunnel = queryType === QueryType.Funnel;

    const schemaId =
      isFunnel && cachedFunnel
        ? (cachedFunnel.activeSchemaId as string)
        : nextStep && nextStep.targetType === queryType
          ? nextStep.builderInfo.schema.id
          : cachedCohort
            ? cachedCohort.schemaId
            : defaultBuilderSchemaId;

    const configQuery = builderConfigByQueryType[queryType];

    dispatch(setBuilderInitialized(false));

    const { data: configData } = await apolloClient.query({
      query: configQuery,
      variables: {
        schemaId,
      },
    });

    // Apply cachedFunnel to a Builder
    // There are 2 scenarios:
    // 1). Create Funnel from Path;
    // 2). Show In Report
    if (isFunnel && cachedFunnel) {
      batch(() => {
        dispatch(resetBuilder());
        dispatch(
          populateConfig(
            prepareBuilderInitialConfig({ queryType, schemaId, ...configData }),
          ),
        );
        populateFunnelState(dispatch, cachedFunnel);
        // Once data transferred, clean it up
        dispatch(clearCachedFunnel());
        dispatch(setBuilderInitialized(true));
      });

      return;
    }

    // Apply next step to a Builder:
    if (nextStep && nextStep.targetType === queryType) {
      batch(() => {
        dispatch(resetBuilder());
        dispatch(
          populateConfig(
            prepareBuilderInitialConfig({ queryType, schemaId, ...configData }),
          ),
        );
        dispatch(populateNextStep(nextStep));
        // Once data transferred, clean it up
        dispatch(clearNextStep());
        dispatch(setBuilderInitialized(true));
      });

      return;
    }

    // Apply cachedCohort to a Builder
    // Cached cohort is used for "Create Cohort" feature:
    // Data can be transferred from Query/Funnel/Retention to Create Cohort
    // And for Inspect feature
    if (cachedCohort) {
      batch(() => {
        dispatch(resetBuilder());
        dispatch(
          populateConfig(
            prepareBuilderInitialConfig({ queryType, schemaId, ...configData }),
          ),
        );
        dispatch(populateCachedCohort(cachedCohort));
        dispatch(setBuilderInitialized(true));
      });

      return;
    }

    // Regular load process:
    batch(() => {
      dispatch(resetBuilder());
      dispatch(
        populateConfig(
          prepareBuilderInitialConfig({ queryType, schemaId, ...configData }),
        ),
      );
      dispatch(setBuilderInitialized(true));
    });
  };

interface IPopulateNamedCohortPayload {
  namedCohortId: string;
}

/**
 * A thunk that handle Named Cohort definition:
 * 1. load named cohort definition;
 * 2. once loaded, get schemaId and load config
 * 3. once config loaded - populate values to redux and show Builder
 */
export const populateNamedCohortThunk =
  (payload: IPopulateNamedCohortPayload): AppThunk =>
  async (dispatch, _, { apolloClient }) => {
    const { namedCohortId } = payload;
    const configQuery = builderConfigByQueryType[QueryType.CohortSample];

    dispatch(setBuilderInitialized(false));

    const { data } = await apolloClient.query<
      INamedCohortQueryQuery,
      INamedCohortQueryQueryVariables
    >({
      query: GET_NAMED_COHORT,
      variables: {
        id: namedCohortId,
      },
    });
    const namedCohort = data?.namedCohort;
    assertNotNil(namedCohort);
    const schemaId = namedCohort.schema.id;

    const { data: configData } = await apolloClient.query({
      query: configQuery,
      variables: {
        schemaId,
      },
    });

    batch(() => {
      dispatch(resetBuilder());
      dispatch(
        populateConfig(
          prepareBuilderInitialConfig({
            queryType: QueryType.CohortSample,
            schemaId,
            ...configData,
          }),
        ),
      );
      dispatch(
        populateNamedBuilderCohort({
          cohort: namedCohort,
          rowMeta: {
            rowType: RowType.builderCohorts,
            rowIndex: 0,
          },
        }),
      );
      dispatch(setBuilderInitialized(true));
    });
  };
